import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { AnalyticsPeriod, AnalyticsTab } from "../../urls";

interface AnalyticsFilterBarProps {
  activeTab: AnalyticsTab;
  onTabChange: (tab: AnalyticsTab) => void;
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  slot: string;
  onSlotChange: (slot: string) => void;
  onRefresh: () => void;
  loading: boolean;
  onDownloadReport?: (format: "pdf" | "csv") => void;
  downloadingFormat?: "pdf" | "csv" | null;
}

const TABS: { id: AnalyticsTab; label: string }[] = [
  { id: "sales", label: "Sales & Trends" },
  { id: "products", label: "Product Performance" },
  { id: "customers", label: "Customer Insights" },
  { id: "inventory", label: "Inventory Insights" },
  { id: "finance", label: "Finance & P&L" },
  { id: "ledger", label: "Ledger Report Summary" },
];

const PERIODS: { id: AnalyticsPeriod; label: string }[] = [
  { id: "day", label: "Day-wise (Today)" },
  { id: "month", label: "Month-wise" },
  { id: "year", label: "Year-wise" },
  { id: "custom", label: "Custom Range" },
  { id: "ledger", label: "Ledger View" },
];

export const AnalyticsFilterBar: React.FC<AnalyticsFilterBarProps> = ({
  activeTab,
  onTabChange,
  period,
  onPeriodChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  slot,
  onSlotChange,
  onRefresh,
  loading,
  onDownloadReport,
  downloadingFormat,
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={4} marginBottom={6}>
      {/* Primary Navigation Tabs */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        borderBottomWidth={1}
        borderBottomStyle="solid"
        borderColor="default1"
        paddingBottom={2}
      >
        {TABS.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "primary" : "secondary"}
            size="medium"
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </Box>

      {/* Filter Controls Row */}
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
        backgroundColor="default2"
        padding={4}
        borderRadius={3}
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
      >
        {/* Period Selector Buttons */}
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Text size={2} fontWeight="bold" color="default1">
            Time View:
          </Text>
          {PERIODS.map(p => (
            <Button
              key={p.id}
              variant={period === p.id ? "primary" : "tertiary"}
              size="small"
              onClick={() => onPeriodChange(p.id)}
            >
              {p.label}
            </Button>
          ))}
        </Box>

        {/* Date Boundaries & Slot Picker */}
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={3}>
          {(period === "custom" || period === "ledger" || period === "day") && (
            <Box display="flex" alignItems="center" gap={2}>
              <Text size={2} color="default1">
                From:
              </Text>
              <input
                type="date"
                value={startDate}
                onChange={e => onStartDateChange(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  fontSize: "13px",
                  fontFamily: "inherit",
                }}
              />
              {period !== "day" && (
                <>
                  <Text size={2} color="default1">
                    To:
                  </Text>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => onEndDateChange(e.target.value)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                  />
                </>
              )}
            </Box>
          )}

          {/* Delivery Slot Filter */}
          <Box display="flex" alignItems="center" gap={2}>
            <Text size={2} color="default1">
              Slot:
            </Text>
            <select
              value={slot}
              onChange={e => onSlotChange(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                fontSize: "13px",
                fontFamily: "inherit",
                backgroundColor: "white",
              }}
            >
              <option value="ALL">All Slots</option>
              <option value="Morning">Morning (09:00 AM - 11:00 AM)</option>
              <option value="Evening">Evening (05:00 PM - 07:00 PM)</option>
            </select>
          </Box>

          {/* Refresh Button */}
          <Button variant="secondary" size="small" disabled={loading} onClick={onRefresh}>
            {loading ? "Updating..." : "Refresh"}
          </Button>

          {/* Quick jump to Report Summary view */}
          {activeTab !== "ledger" && (
            <Button variant="secondary" size="small" onClick={() => onTabChange("ledger")}>
              View Report Summary
            </Button>
          )}

          {/* Download Report Actions */}
          {onDownloadReport && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="secondary"
                size="small"
                disabled={loading || !!downloadingFormat}
                onClick={() => onDownloadReport("pdf")}
              >
                {downloadingFormat === "pdf" ? "PDF..." : "Download PDF"}
              </Button>
              <Button
                variant="secondary"
                size="small"
                disabled={loading || !!downloadingFormat}
                onClick={() => onDownloadReport("csv")}
              >
                {downloadingFormat === "csv" ? "CSV..." : "Download CSV"}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
